import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check } from 'lucide-react';
import SageDataTable from './SageDataTable';

function formatSql(raw) {
  if (!raw) return raw;
  // Keywords that start a new line
  const keywords = ['SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'OFFSET', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'FULL JOIN', 'CROSS JOIN', 'ON', 'UNION', 'EXCEPT', 'INTERSECT', 'WITH', 'AS ('];
  let s = raw.replace(/\s+/g, ' ').trim();
  // Insert newlines before each keyword (case-insensitive)
  for (const kw of keywords) {
    const re = new RegExp(`\\s+(${kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    s = s.replace(re, `\n${kw}`);
  }
  // Indent continuation lines (everything except first-level keywords)
  const lines = s.split('\n');
  const topLevel = ['SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'OFFSET', 'UNION', 'EXCEPT', 'INTERSECT', 'WITH'];
  return lines.map((line) => {
    const trimmed = line.trim();
    const isTop = topLevel.some((kw) => trimmed.toUpperCase().startsWith(kw));
    return isTop ? trimmed : '  ' + trimmed;
  }).join('\n');
}

export default function SageChatMessage({ role, text, sql, data, isError, streaming }) {
  const [showSql, setShowSql] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopySql = () => {
    navigator.clipboard.writeText(sql).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (role === 'user') {
    return (
      <div className="flex justify-end mb-3">
        <div className="max-w-[85%] bg-amber-50 border border-amber-200 rounded-xl rounded-br-sm px-3.5 py-2.5">
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{text}</p>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex justify-start mb-3">
      <div className="flex gap-2.5 max-w-[90%]">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-white
                        flex items-center justify-center text-xs font-bold mt-0.5">
          S
        </div>
        <div className="flex-1 min-w-0">
          <div className={`rounded-xl rounded-tl-sm px-3.5 py-2.5 ${
            isError
              ? 'bg-red-50 border border-red-200'
              : 'bg-white border border-gray-200'
          }`}>
            {text ? (
              <div className="prose prose-sm max-w-none overflow-x-auto
                              prose-p:my-1.5 prose-headings:my-2
                              prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5
                              prose-table:my-2 prose-pre:my-2
                              prose-td:px-2 prose-td:py-1 prose-th:px-2 prose-th:py-1
                              prose-table:text-xs
                              prose-code:text-amber-700 prose-code:bg-amber-50 prose-code:rounded prose-code:px-1
                              prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg
                              prose-a:text-amber-600">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
              </div>
            ) : streaming ? (
              <div className="flex items-center gap-2 py-1">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-400">Analyzing your data...</span>
              </div>
            ) : null}
            {streaming && text && (
              <span className="inline-block w-1.5 h-4 bg-amber-400 animate-pulse ml-0.5 -mb-0.5 rounded-sm" />
            )}
          </div>

          {/* SQL toggle */}
          {sql && !streaming && (
            <div className="mt-1.5">
              <button
                onClick={() => setShowSql(!showSql)}
                className="text-xs text-gray-400 hover:text-amber-600 transition-colors"
              >
                {showSql ? 'Hide SQL' : 'View SQL'}
              </button>
              {showSql && (
                <div className="relative mt-1">
                  <button
                    onClick={handleCopySql}
                    className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-1 rounded
                               text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
                    title="Copy SQL"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                  <pre className="p-2.5 pr-10 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-x-auto">
                    <code>{formatSql(sql)}</code>
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Data table */}
          {data && !streaming && <SageDataTable data={data} />}
        </div>
      </div>
    </div>
  );
}
